import React from 'react';

import useAsync, { useAsyncFn } from 'Hooks/useAsync';
import { useMounted } from 'Hooks/useMounted';

/**
 * Wrapper for creating hooks that run queries safely
 * when the component is mounted.
 *
 * @param query query function. Returns the data that should be
 * the return type. Query params can be contained in it as a
 * closure, but they should also be included dependencies.
 * @param deps list of dependencies to trigger a query to be
 * rerun.
 *
 * @returns Data the query resolves to
 */
const useMountedQuery = <ReturnData, >(
	query: useAsyncFn<ReturnData>,
	deps?: readonly any[],
	onAfterQuery?: (responseData: ReturnData, isComponentMounted: boolean) => any,
	skip?: boolean,
) => {
	const mounted = useMounted();

	return useAsync(
		async abortSignal => {
			if (skip) {
				return;
			}

			const queryResponse = await query(abortSignal);

			// if request hasn't been aborted and we have a response
			if (!abortSignal.aborted && queryResponse !== undefined) {
				onAfterQuery?.(queryResponse, mounted.current);
			}

			return queryResponse;
		},
		deps,
	);
};

export default useMountedQuery;
